/**
 * Persona Creation Modal
 * 
 * Triggered when unauthenticated users try to rate/save content.
 * Minimal friction: only display name required.
 * Creates persona and persists intended action after creation.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, X, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

function PersonaCreationModal({ isOpen, onClose, onSuccess, pendingAction }) {
    const { isAuthenticated, fetchPersonas, getAuthHeaders } = useAuth();

    const [formData, setFormData] = useState({
        display_name: '',
        bio: '',
        pronouns: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (error) setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.display_name.trim() || formData.display_name.length < 2) {
            setError('Display name must be at least 2 characters');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/profiles/persona`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify({
                    display_name: formData.display_name.trim(),
                    bio: formData.bio.trim() || null,
                    pronouns: formData.pronouns.trim() || null,
                    visibility: 'private',
                    is_primary: true
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to create persona');
            }

            const { persona } = await response.json();

            // Refresh personas in auth context
            await fetchPersonas();

            // Call success callback with new persona and pending action
            if (onSuccess) {
                onSuccess(persona, pendingAction);
            }

            onClose();
        } catch (err) {
            console.error('Persona creation error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // If not authenticated, show login prompt instead
    if (!isAuthenticated) {
        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/90 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative max-w-md w-full mx-4 bg-[#0A0A0A] border border-wine-700/30 shadow-2xl p-8"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-dark-400 hover:text-cream transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-wine-900/30 flex items-center justify-center">
                                <User className="w-8 h-8 text-wine-400" />
                            </div>
                            <h2 className="text-xl font-display font-bold text-cream mb-2">
                                Sign In to Save
                            </h2>
                            <p className="text-cream/60 text-sm mb-6">
                                Create an account to save your preferences and build your personal kink profile.
                            </p>
                            <div className="flex gap-3">
                                <a
                                    href="/login"
                                    className="flex-1 btn-primary text-center py-3"
                                >
                                    Sign In
                                </a>
                                <a
                                    href="/register"
                                    className="flex-1 btn-secondary text-center py-3"
                                >
                                    Register
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/90 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative max-w-md w-full mx-4 bg-[#0A0A0A] border border-wine-700/30 shadow-2xl"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-wine-800/30">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-dark-400 hover:text-cream transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-wine-900/30 flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-[#C4A35A]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-display font-bold text-cream">
                                    Create a Persona
                                </h2>
                                <p className="text-sm text-cream/50">
                                    A facet of your desires—dom, sub, or something else entirely
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-5">
                        {/* Display Name - Required */}
                        <div>
                            <label className="block text-sm font-medium text-cream/80 mb-2">
                                Display Name <span className="text-wine-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.display_name}
                                onChange={(e) => handleChange('display_name', e.target.value)}
                                placeholder="Your explorer name..."
                                className="input"
                                maxLength={50}
                                autoFocus
                            />
                            <p className="text-xs text-cream/40 mt-1">
                                e.g., "My Dom Side", "Curious Explorer", "Luna (OC)"
                            </p>
                        </div>

                        {/* Pronouns - Optional */}
                        <div>
                            <label className="block text-sm font-medium text-cream/80 mb-2">
                                Pronouns <span className="text-cream/40">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={formData.pronouns}
                                onChange={(e) => handleChange('pronouns', e.target.value)}
                                placeholder="e.g., she/her, they/them"
                                className="input"
                                maxLength={20}
                            />
                        </div>

                        {/* Bio - Optional */}
                        <div>
                            <label className="block text-sm font-medium text-cream/80 mb-2">
                                Bio <span className="text-cream/40">(optional)</span>
                            </label>
                            <textarea
                                value={formData.bio}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                placeholder="A brief description of your interests..."
                                className="input resize-none h-20"
                                maxLength={500}
                            />
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="bg-red-900/20 border border-red-500/30 text-red-400 text-sm px-4 py-3">
                                {error}
                            </div>
                        )}

                        {/* Privacy Note */}
                        <div className="bg-wine-900/10 border border-wine-800/30 p-4 text-xs text-cream/50">
                            <strong className="text-cream/70">Privacy First:</strong> Your persona is private by default.
                            Your ratings and preferences are never shared without your explicit consent.
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 btn-ghost py-3"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !formData.display_name.trim()}
                                className="flex-1 btn-primary py-3 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" />
                                        Create Persona
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default PersonaCreationModal;
